import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { recordFeatureUsage } from '@busy/api';
import { TopBar } from './TopBar';
import { BottomTabs } from './BottomTabs';
import { BusyHelper } from './BusyHelper';

/** Strip a pathname to a stable route key for the feature_usage_event
 *  aggregate. We aggregate at the top level — '/clients/123' rolls up
 *  to 'clients' so the table stays bounded by route family. */
function routeKeyFromPath(pathname: string): string | null {
  const trimmed = pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  if (!trimmed) return null;
  // Only authenticated app routes — skip onboarding so the funnel
  // events table can own that signal cleanly.
  const allowed = new Set([
    'today',
    'dashboard',
    'lessons',
    'clients',
    'cuts',
    'settings',
    'achievements',
  ]);
  return allowed.has(trimmed) ? trimmed : null;
}

export function AppShell() {
  // Feature usage tracker (migration 0069). Increments the daily
  // counter for the matched route on every navigation. The RPC
  // upserts (user_id, today, route_key) so 12 visits to /cut-timer
  // in a day → one row with visit_count=12, not 12 rows.
  const location = useLocation();
  useEffect(() => {
    const key = routeKeyFromPath(location.pathname);
    if (!key) return;
    void recordFeatureUsage(key);
  }, [location.pathname]);

  return (
    // min-h-[100dvh] adapts to iOS Safari's collapsing chrome — when the
    // URL bar shows up mid-scroll, the page resizes correctly instead of
    // overflowing behind the chrome. The pb on <main> clears the BottomTabs
    // height (h-16 + pb-safe) so primary CTAs aren't covered by the nav.
    <div className="min-h-[100dvh] flex flex-col bg-bg">
      <TopBar />
      <main className="flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-safe">
        <Outlet />
      </main>
      <BottomTabs />
      <BusyHelper />
    </div>
  );
}
